/**
 * Files sidebar: "Sync to this computer" in the desktop app (opens the sync panel when sync is set up, else the setup
 * window; files-desktop-sync.md), or "Get the desktop app" in a desktop browser (the download page).
 */
import { Button, Stack, useT } from "@bundle/ui";
import { useEffect, useState } from "react";
import { isDesktop, openInBrowser } from "../platform/desktop.ts";
import { canOpenInDesktopApp, DESKTOP_DOWNLOAD_URL } from "../platform/desktopApp.ts";
import { desktopSync, type SyncState } from "../platform/desktopSync.ts";

export function DesktopSyncEntry({ workspaceSlug }: { workspaceSlug: string }) {
  const t = useT();
  const desktop = isDesktop();
  // undefined = loading; null = this app build has no sync engine (hide the entry).
  const [state, setState] = useState<SyncState | null>();
  useEffect(() => {
    if (!desktop) return;
    const sync = desktopSync(false);
    let alive = true;
    let unlisten: (() => void) | undefined;
    sync.status().then(
      (s) => alive && setState(s.state),
      () => alive && setState(null),
    );
    void sync.onStatus((s) => alive && setState(s.state)).then((u) => {
      if (alive) unlisten = u;
      else u();
    });
    return () => {
      alive = false;
      unlisten?.();
    };
  }, [desktop]);

  if (!desktop) {
    if (!canOpenInDesktopApp()) return null;
    return (
      <Stack direction="horizontal" paddingX="2" paddingY="1">
        <Button label={t("sync.files.getApp")} icon="download" variant="ghost" size="sm" onPress={() => void openInBrowser(DESKTOP_DOWNLOAD_URL)} testID="files-get-desktop-app" />
      </Stack>
    );
  }
  if (state === null || state === undefined) return null;
  const sync = desktopSync(false);
  const on = state !== "off";
  return (
    <Stack direction="horizontal" paddingX="2" paddingY="1">
      <Button
        label={on ? t("sync.files.entryOn") : t("sync.files.entry")}
        icon="cloud"
        variant="ghost"
        size="sm"
        onPress={() => void (on ? sync.openPanel() : sync.openSetup(workspaceSlug))}
        testID="files-desktop-sync"
      />
    </Stack>
  );
}
